/* eslint-disable react/jsx-filename-extension */
import '@chegg-wt/core/theme/_index.scss'
import '@chegg-wt/core/theme/form.scss'

import { colors } from '@chegg-ui/theme'
import App from '@chegg-wt/core/components/App'
import DeIndex from '@chegg-wt/core/components/Head/DeIndex'
import MetaDescription from '@chegg-wt/core/components/Head/MetaDescription'
import createStore from '@chegg-wt/core/state'
import { getBackendOptsSafe } from '@chegg-wt/core/utils/backendOpts'
import { getCurrentProjectPreloadedState } from '@chegg-wt/core/utils/currentProject'
import { isServerSideContext } from '@chegg-wt/core/utils/isServerSideContext'
import { getActualStyleId } from '@chegg-wt/core/utils/styles'
import { isAdsFreeURL, isNonCanonicalRoute } from '@chegg-wt/core/utils/url'
import { getUserPreloadedState } from '@chegg-wt/core/utils/user'
import { initializeAmplitude } from '@chegg/amplitude-analytics'
import cookies from 'next-cookies'
import NextApp from 'next/app'
import { withRouter } from 'next/router'
import { Provider } from 'react-redux'

import { head } from '../config/head'
import { navItemsSelector } from '../config/nav'
import { routes } from '../config/routes'
import { footerLinkColumns } from '../copy/footerLinkColumns'
import { legalContents } from '../copy/legalContents'
import siteEnv from '../state/env'
import { EBTheme } from '../theme'
import {
  getSourceFromQuery,
  getStyleFromQuery,
  isSourceExact,
  isStyleExact,
} from '../utils/routes'

const makeStore = (preloadedState = {}) => {
  return createStore(routes, siteEnv, preloadedState)
}

class CitationsApp extends NextApp {
  // Note: this will disable automatic static optimization
  // see here: https://nextjs.org/docs#automatic-static-optimization
  // we want `getInitialProps` here to set query object when loaded on server

  static async getInitialProps({ Component, ctx }) {
    const pageProps = Component.getInitialProps
      ? await Component.getInitialProps(ctx)
      : {}

    const featureFlags = isServerSideContext(ctx) ? await getBackendOptsSafe(ctx) : {}

    const { userRole } = cookies(ctx)
    const preloadedState = {
      featureFlags,
      user: getUserPreloadedState(userRole),
    }

    return { pageProps, preloadedState, isAdsFree: isAdsFreeURL(ctx.pathname) }
  }

  componentDidMount() {
    // Initialize amplitude analytics
    if (typeof window !== 'undefined') {
      initializeAmplitude()
    }
  }

  render() {
    const { Component, pageProps, preloadedState, isAdsFree, router } = this.props
    const currentProject = getCurrentProjectPreloadedState({
      getStyleFromQuery,
      isStyleExact,
      router,
      getActualStyleId,
    })
    const store = makeStore({ ...preloadedState, currentProject })

    const config = {
      head,
      navItemsSelector,
      routes,
      siteEnv,
      theme: EBTheme,
      footerContents: {
        footerLinkColumns,
        legalContents,
      },
      getSourceFromQuery,
      isSourceExact,
      getStyleFromQuery,
      isStyleExact,
      isAdsFree,
      getActualStyleId,
    }

    const getPageLayout = Component.getPageLayout || ((page) => page)

    const getSeoComponent = (router) => {
      const { route } = router
      const isNonCanonical = isNonCanonicalRoute(route)
      if (isNonCanonical) return <DeIndex />
    }

    const { route } = router
    const showBanner = route !== '/upgrade'
    const isHomePage = route === '/'

    return (
      <Provider store={store}>
        {getSeoComponent(this.props.router)}
        <MetaDescription content="Automatic works cited and bibliography formatting for MLA, APA and Chicago/Turabian citation styles. Now supports MLA 9." />
        <App
          {...config}
          showBanner={showBanner}
          isHomePage={isHomePage}
          fullWidthBannerProps={{
            backgroundColour: colors.yellow100,
            minHeight: '90px',
          }}
        >
          {getPageLayout(<Component {...pageProps} />, pageProps)}
        </App>
      </Provider>
    )
  }
}

export default withRouter(CitationsApp)
